/**
 * LECTURA DEL DORSO impreso, por geometría.
 *
 * La otra mitad del frente, y con una regla geométrica DISTINTA que también es
 * propiedad del formato: en el dorso el valor va AL LADO del rótulo, en el mismo
 * renglón —"DOMICILIO: ALSINA 1234", "CUIL: 27-31845220-8"—, mientras que en
 * el frente va debajo porque el frente es una grilla.
 *
 * Los campos son los que enumera la Disposición 55/2026 para el reverso:
 * domicilio, lugar de nacimiento, CUIL y número de trámite. El modelo 2012 tiene
 * los mismos salvo el trámite, que ahí va en el frente.
 *
 * Esto NO lee el MRZ: de eso se ocupa `mrzTd1`, que además tiene con qué
 * probarse. Acá todo es lectura única.
 *
 * Antes de estar enganchado esto leía mal, y la causa no estaba acá: las fotos
 * de dorso entraban al OCR a su tamaño original y con caracteres de 8 px no se
 * reconoce ningún rótulo. Con la imagen llevada a su tamaño útil el dorso de un
 * DNI de extranjero pasa a dar domicilio, país de nacimiento, categoría y CUIL.
 *
 * Lo que sigue pendiente es leer por TEMPLATE: con la tarjeta ubicada y el
 * modelo reconocido la posición de cada zona ya se conoce y no hace falta
 * encontrar el rótulo. Buscar por rótulo queda como respaldo para cuando no se
 * pudo ubicar la tarjeta.
 */
import { PalabraOcr } from "../dniMatch";
import { Hallazgo } from "./hallazgos";
import { Rectangulo } from "./types";
import { DefinicionDeRotulo, ROTULOS, buscarRotulos, esDeOtroRotulo, filas } from "./frente";
import { esCuitValido } from "../cuit";
import { fechaDelFrente } from "./frente";

/** Rótulos del dorso. Mismo criterio que los del frente: sinónimos con tolerancia. */
export const ROTULOS_DORSO: Record<string, DefinicionDeRotulo> = {
  domicilio: { claves: ["DOMICILIO", "ADDRESS"] },
  // "LUGAR" y no "NACIMIENTO": la palabra suelta también aparece en "FECHA DE
  // NACIMIENTO" del frente y, peor, la coincidencia difusa la hacía enganchar
  // con la fila del domicilio.
  lugarNacimiento: { claves: ["LUGAR", "BIRTHPLACE"] },
  cuil: { claves: ["CUIL", "CUIT"] },
  numeroTramite: { claves: ["TRAMITE"] },
  // El dorso del DNI de EXTRANJERO agrega su propio juego de campos. No son
  // otro formato: son los mismos rótulos impresos de la misma manera, y por eso
  // entran como datos en esta tabla y no como lógica nueva.
  //
  // "PAIS" y no "NACIMIENTO": la palabra suelta también está en "FECHA DE
  // NACIMIENTO" del frente y en "LUGAR DE NACIMIENTO" de acá.
  paisNacimiento: { claves: ["PAIS"], excluye: ["INGRESO"] },
  fechaIngreso: { claves: ["INGRESO"], excluye: ["CATEGORIA"] },
  categoriaIngreso: { claves: ["CATEGORIA"] },
  expediente: { claves: ["EXPEDIENTE"] },
  disposicion: { claves: ["DISPOSICION"] },
  fechaRadicacion: { claves: ["RADICACION"] },
};

function unir(ws: PalabraOcr[]): Rectangulo {
  const x = Math.min(...ws.map((w) => w.bbox.x0));
  const y = Math.min(...ws.map((w) => w.bbox.y0));
  return {
    x,
    y,
    w: Math.max(...ws.map((w) => w.bbox.x1)) - x,
    h: Math.max(...ws.map((w) => w.bbox.y1)) - y,
  };
}

export interface ValorAlLado {
  texto: string;
  caja: Rectangulo;
}

/**
 * El valor que sigue al rótulo en su mismo renglón.
 *
 * Se toma todo lo que hay a la derecha del rótulo hasta el final del renglón, y
 * se continúa en el renglón siguiente cuando el valor es largo: el domicilio
 * admite 51 caracteres según la norma y no entra en una línea.
 */
/**
 * ¿Esta palabra CIERRA un rótulo?
 *
 * En el dorso el rótulo termina en dos puntos: "DOMICILIO:", "PAÍS DE
 * NACIMIENTO:", "CUIL:". Eso es lo que separa el rótulo de su valor cuando el
 * rótulo tiene varias palabras, y también lo que marca dónde empieza el rótulo
 * SIGUIENTE en un renglón que lleva dos campos —"EXPEDIENTE: 920271970
 * DISPOSICIÓN: 38"—.
 *
 * El punto también cuenta, pero solo en una palabra sin dígitos: el OCR lee los
 * dos puntos como punto, y un punto entre números es un separador de miles.
 */
function cierraRotulo(w: PalabraOcr): boolean {
  const t = w.text.trim();
  if (/[:;]$/.test(t)) return true;
  return t.length >= 4 && /^[^\d]+\.$/.test(t);
}

/** Lo que no tiene ni una letra ni un dígito no es parte de un valor. */
const conContenido = (w: PalabraOcr) => /[A-Za-z0-9]/.test(w.text);

export function valorAlLadoDeRotulo(
  palabras: PalabraOcr[],
  def: DefinicionDeRotulo,
  renglonesExtra = 0
): ValorAlLado | null {
  const todas = filas(palabras);
  const tabla = { ...ROTULOS, ...ROTULOS_DORSO };
  for (const { fila, ancla } of buscarRotulos(palabras, def, tabla)) {
    const desdeAncla = fila
      .filter((w) => w.bbox.x0 >= ancla.bbox.x0)
      .sort((a, b) => a.bbox.x0 - b.bbox.x0);

    // El rótulo termina en sus dos puntos. Sin eso, el valor de "PAÍS DE
    // NACIMIENTO: CHILE" salía "DE NACIMIENTO: CHILE": el ancla es la primera
    // palabra del rótulo, no la última.
    const cierre = desdeAncla.findIndex(cierraRotulo);
    let derecha =
      cierre >= 0
        ? desdeAncla.slice(cierre + 1)
        : desdeAncla.filter((w) => w.bbox.x0 >= ancla.bbox.x1);

    // Y el valor termina donde empieza el rótulo siguiente.
    const otro = derecha.findIndex(
      (w) => cierraRotulo(w) || esDeOtroRotulo(w, def.claves, tabla)
    );
    if (otro >= 0) derecha = derecha.slice(0, otro);
    derecha = derecha.filter(conContenido);
    if (derecha.length === 0) continue;

    const partes = [...derecha];
    if (renglonesExtra > 0) {
      // Se busca por CONTENIDO, no por identidad: `buscarRotulos` arma sus
      // propias filas y no son los mismos arreglos que éstas. Comparando por
      // referencia el índice daba -1 y el valor no se continuaba NUNCA — el
      // domicilio se quedaba siempre en su primera línea.
      const i = todas.findIndex((f) => f.includes(ancla));
      let agregados = 0;
      for (let n = 1; i >= 0 && i + n < todas.length && agregados < renglonesExtra; n++) {
        const siguiente = todas[i + n].filter(conContenido);
        // Los renglones VACÍOS no cuentan. El OCR devuelve renglones de una
        // comilla o un guión entre dos líneas de texto, y gastaban la única
        // continuación permitida: el domicilio se quedaba sin su segunda línea.
        if (siguiente.length === 0) continue;
        // Un renglón que arranca otro rótulo NO es continuación de este valor.
        if (siguiente.some((w) => cierraRotulo(w) || esDeOtroRotulo(w, def.claves, tabla))) break;
        partes.push(...siguiente);
        agregados++;
      }
    }
    const texto = partes.map((w) => w.text.trim()).join(" ").replace(/\s+/g, " ").trim();
    const limpio = texto.replace(/^[:.\-\s]+/, "").trim();
    if (limpio) return { texto: limpio, caja: unir(partes) };
  }
  return null;
}

/**
 * Palabras de dos letras para arriba, en mayúsculas y sin acentos.
 *
 * El OCR deja colgando lo que no supo leer —"CHILE Z===" por "CHILE"—, y una
 * letra suelta no es parte de un país ni de una categoría.
 */
function soloPalabras(t: string): string | null {
  const limpio = t
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z ]/g, " ")
    .split(/\s+/)
    .filter((p) => p.length >= 2)
    .join(" ")
    .trim();
  return limpio || null;
}

/** Un número de expediente o de disposición: solo los dígitos. */
function soloDigitos(t: string): string | null {
  const d = t.replace(/\D/g, "");
  return d.length >= 2 ? d : null;
}

/** Todo lo que el dorso impreso dice, como hallazgos. */
export function hallazgosDelDorso(palabras: PalabraOcr[]): Hallazgo[] {
  const salida: Hallazgo[] = [];

  // El domicilio puede ocupar dos renglones: la norma le da 51 caracteres.
  const domicilio = valorAlLadoDeRotulo(palabras, ROTULOS_DORSO.domicilio, 1);
  if (domicilio) {
    salida.push({
      campo: "domicilio",
      valor: domicilio.texto.toUpperCase(),
      crudo: domicilio.texto,
      origen: "impreso",
      caja: domicilio.caja,
    });
  }

  const lugar = valorAlLadoDeRotulo(palabras, ROTULOS_DORSO.lugarNacimiento);
  if (lugar) {
    salida.push({
      campo: "lugarNacimiento",
      valor: lugar.texto.toUpperCase(),
      crudo: lugar.texto,
      origen: "impreso",
      caja: lugar.caja,
    });
  }

  const cuil = valorAlLadoDeRotulo(palabras, ROTULOS_DORSO.cuil);
  if (cuil) {
    const digitos = cuil.texto.replace(/\D/g, "");
    // El CUIL se descarta si no cierra su módulo 11. Un CUIL mal leído es peor
    // que ninguno: entra derecho al campo fiscal del autor y nadie lo revisa.
    if (digitos.length === 11 && esCuitValido(digitos)) {
      salida.push({
        campo: "cuil",
        valor: `${digitos.slice(0, 2)}-${digitos.slice(2, 10)}-${digitos.slice(10)}`,
        crudo: cuil.texto,
        origen: "impreso",
        caja: cuil.caja,
        // El módulo 11 es un dígito de control: si cierra, la lectura está probada.
        probado: true,
      });
    }
  }

  // Los campos del DNI de extranjero. Son texto al lado de su rótulo, igual que
  // el resto, así que se leen con el mismo mecanismo: una tabla, no una rama.
  //
  // Cada uno declara CÓMO es su valor, que es lo que deja afuera la basura que
  // el OCR pega al final: "CHILE Z===" es CHILE, y "19 MAY 2020\"" es una fecha.
  const extranjero: [string, (t: string) => string | null][] = [
    ["paisNacimiento", soloPalabras],
    ["fechaIngreso", fechaDelFrente],
    ["categoriaIngreso", soloPalabras],
    ["expediente", soloDigitos],
    ["disposicion", soloDigitos],
    ["fechaRadicacion", fechaDelFrente],
  ];
  for (const [campo, comoEs] of extranjero) {
    const v = valorAlLadoDeRotulo(palabras, ROTULOS_DORSO[campo]);
    if (!v) continue;
    const valor = comoEs(v.texto);
    if (!valor) continue;
    salida.push({ campo, valor, crudo: v.texto, origen: "impreso", caja: v.caja });
  }

  const tramite = valorAlLadoDeRotulo(palabras, ROTULOS_DORSO.numeroTramite);
  if (tramite) {
    const digitos = tramite.texto.replace(/\D/g, "");
    if (digitos.length >= 10) {
      salida.push({
        campo: "numeroTramite",
        valor: digitos,
        crudo: tramite.texto,
        origen: "impreso",
        caja: tramite.caja,
      });
    }
  }

  return salida;
}
